import { createSelector } from '@reduxjs/toolkit';
import { AbstractedHardwareOverviewResponse } from '@vfde-sails/glados-v2';
import { RootState } from '../../store';
import {
    selectPaymentMethod,
    selectSelectedCategories,
    selectSelectedProductId,
} from './slice';
import { getDeviceOverview } from './api/glados';
import { createStructuredSelector } from 'reselect';

const createAppSelector = createSelector.withTypes<RootState>();

const getSelectorForDeviceOverviewQuery = createAppSelector(
    selectPaymentMethod,
    paymentMethod => getDeviceOverview.select(paymentMethod),
);

const selectDeviceOverviewQuery = createAppSelector(
    [
        state => state,
        getSelectorForDeviceOverviewQuery,
    ],
    (state, selector) => selector(state),
);

const selectLoading = createAppSelector(
    selectDeviceOverviewQuery,
    query => query.isLoading,
);

const selectError = createAppSelector(
    selectDeviceOverviewQuery,
    query => query.isError,
);

const selectDevices = createAppSelector(
    selectDeviceOverviewQuery,
    query => query.data?.devices || [],
);

const selectAvailableCategories = createAppSelector(
    selectDeviceOverviewQuery,
    query => query.data?.categories || [],
);

const selectNumberOfDevices =
    createAppSelector(
        selectDeviceOverviewQuery,
        query => query.data?.totalDevices || 0,
    );

const selectDevicesByCategory = createAppSelector(
    [
        selectDevices,
        selectSelectedCategories,
    ],
    (devices, selectedCategories) => {
        if (selectedCategories.length === 0) {
            return devices;
        }

        // select those devices where at least one of the device facets matches one of the selected facet
        return devices.filter(device =>
            selectedCategories.some(category =>
                device.categories.some(deviceCategory => category.label === deviceCategory.label),
            ),
        );
    },
);

const selectSelectedProduct = createAppSelector(
    [
        selectSelectedProductId,
        selectDevices,
    ],
    (selectedProductId, devices): AbstractedHardwareOverviewResponse['devices'][number] | null => {
        if (!selectedProductId || devices.length === 0) {
            return null;
        }

        return devices.filter(device => device.defaultAtomicId === selectedProductId)[0] || null;
    },
);

const createStructuredAppSelector =
    createStructuredSelector.withTypes<RootState>();

export const mapStateToProps = createStructuredAppSelector({
    isLoading: selectLoading,
    hasError: selectError,
    devices: selectDevicesByCategory,
    numberOfDevices: selectNumberOfDevices,
    availableCategories: selectAvailableCategories,
    selectedCategories: selectSelectedCategories,
    paymentMethod: selectPaymentMethod,
});

export {
    selectLoading,
    selectError,
    selectDevices,
    selectAvailableCategories,
    selectNumberOfDevices,
    selectSelectedCategories,
    selectDevicesByCategory,
    selectSelectedProduct,
    selectPaymentMethod,
};
